import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import useUpdateDiscountSettings from "@babackapp/private-api-client/client/use-update-portal-discount-settings";
import { SettingsFormPageButtonBar } from "@babackapp/react-hook-forms-components/form-page";
import FormSwitch from "@babackapp/react-hook-forms-components/form-switch";
import { Card, CardContent } from "@babackapp/ui/components/card";
import { Form, FormField } from "@babackapp/ui/components/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import DiscountSettingsActivatedForm from "./discount-settings-activated-form";
import useTranslation from "@babackapp/translations/use-translation";

const discountSettingsFormSchema = z.object({
  activated: z.boolean(),
  discount_bonus: z.object({
    amount: z.number(),
    type: z.string(),
  }),
  discountAcceptedCountries: z.array(z.string()),
  tagsForNotAcceptedDiscount: z.array(z.string()),
  minimalDiscountForFreeShipping: z.number(),
  discountFreeShippingActivated: z.boolean(),
  freeShippingCode: z.string(),
  giftCard: z.string(),
  applyReturnShippingFeesForDiscountRule: z.boolean().optional().nullable(),
  validity: z.number(),
  codesToIgnoreRegex: z.string().optional().readonly(),
});

export type DiscountSettingsFormValues = z.infer<
  typeof discountSettingsFormSchema
>;

const DEFAULT_VALUES: DiscountSettingsFormValues = {
  activated: false,
  discount_bonus: {
    amount: 0,
    type: "percentage",
  },
  discountAcceptedCountries: [],
  tagsForNotAcceptedDiscount: [],
  minimalDiscountForFreeShipping: 0,
  discountFreeShippingActivated: false,
  freeShippingCode: "",
  giftCard: "discount",
  applyReturnShippingFeesForDiscountRule: false,
  validity: -1,
  codesToIgnoreRegex: "",
};

type DiscountFormProps = {
  defaultValues?: Partial<DiscountSettingsFormValues>;
};

export default function DiscountFormSettings({
  defaultValues,
}: DiscountFormProps = {}) {
  const t = useTranslation();

  const form = useForm<DiscountSettingsFormValues>({
    resolver: zodResolver(discountSettingsFormSchema),
    defaultValues: {
      ...DEFAULT_VALUES,
      ...defaultValues,
    },
  });

  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updateDiscountSettings } =
    useUpdateDiscountSettings(shop);

  const onSubmit = form.handleSubmit(async (data) =>
    updateDiscountSettings(data),
  );

  return (
    <Card className="w-full">
      <CardContent>
        <Form {...form}>
          <form
            onSubmit={onSubmit}
            className="flex flex-col justify-start gap-8 mt-8"
          >
            <FormField
              control={form.control}
              name="activated"
              render={({ field }) => (
                <FormSwitch
                  label={t("settings.config.discount.activated.title")}
                  description={t(
                    "settings.config.discount.activated.description",
                  )}
                  {...field}
                />
              )}
            />
            <DiscountSettingsActivatedForm />
            <SettingsFormPageButtonBar />
          </form>
        </Form>
      </CardContent>
    </Card>
  );
}
